import Helmet from 'react-helmet';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { Section } from '@sorare/core/src/components/landing/Section';
import { ORIGIN } from '@sorare/core/src/config';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import creatorsAvif from './assets/creators.avif';
import creators from './assets/creators.png';
import sorareCreatorsProgramAvif from './assets/sorare-creators-program.avif';
import sorareCreatorsProgram from './assets/sorare-creators-program.png';
import tileBackground from './assets/tile.jpg';

const HeroContainer = styled(Section)`
  background-color: var(--c-nl-900-opaque);
`;

const CreatorProgramTitleImg = styled(Img)`
  width: calc(var(--unit) * 50);
`;

const CreatorsImg = styled(Img)`
  margin-top: calc(var(--unit) * -5);
  width: 100%;
  object-fit: cover;
`;

const TileWrapper = styled(Vertical)`
  @media ${tabletAndAbove} {
    flex-direction: row;
  }
`;

const StepContainer = styled(Horizontal)`
  align-items: flex-start;
  background: url(${tileBackground}) center;
  background-size: cover;
  border-radius: var(--unit);
  height: calc(var(--unit) * 20);
  width: calc(var(--unit) * 35);
  padding: var(--unit);
`;

const TileName = styled.p`
  color: var(--c-white);
  text-transform: uppercase;
  font-family: var(--brand-font);
  font-size: calc(var(--unit) * 10);
`;

const TileText = styled(Vertical)`
  align-self: center;
`;

const TileTitle = styled.p`
  color: var(--c-white);
  text-transform: uppercase;
  font: var(--t-headline-xs);
  font-family: var(--brand-font);
`;

const messages = defineMessages({
  heroSubtitle: {
    id: 'AffiliateProgram.subtitle',
    defaultMessage:
      'Earn money by promoting Sorare, where you can buy, trade, and play with official digital cards of football, NBA and MLB players.',
  },
  CTAButton: {
    id: 'CreatorProgram.CTAButton',
    defaultMessage: 'Become a creator',
  },
  howToTitle: {
    id: 'CreatorProgram.howTo',
    defaultMessage: 'Become a Sorare creator',
  },
  howToChip: {
    id: 'CreatorProgram.howToChip',
    defaultMessage: 'Join to earn',
  },
});

export const steps = [
  {
    name: '1',
    title: (
      <FormattedMessage
        id="CreatorProgram.howToStep1"
        defaultMessage="Become a creator"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="CreatorProgram.step1Subtitle"
        defaultMessage="It's easy and free to join. After your application is approved, you'll get access to promotional assets."
      />
    ),
  },
  {
    name: '2',
    title: (
      <FormattedMessage
        id="CreatorProgram.step2Title"
        defaultMessage="Promote Sorare"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="CreatorProgram.step2Subtitle"
        defaultMessage="Create content (videos, articles, ads, etc.) and link to your Sorare Creator link."
      />
    ),
  },
  {
    name: '3',
    title: (
      <FormattedMessage
        id="CreatorProgram.step3Title"
        defaultMessage="Earn commission"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="CreatorProgram.step3Subtitle"
        defaultMessage="Get up to 10% in commissions for every qualifying Card purchase you've brought to Sorare."
      />
    ),
  },
];

const SorareCreatorLogo = () => {
  const { formatMessage } = useIntl();
  const description = formatMessage({
    id: 'CreatorProgram.sorareCreatorLogoDescription',
    defaultMessage: "An image displaying the title 'Sorare Creators Program'.",
  });
  return (
    <Vertical gap={1} center>
      <picture>
        <source srcSet={sorareCreatorsProgramAvif} type="image/avif" />
        <CreatorProgramTitleImg
          src={sorareCreatorsProgram}
          fetchpriority="low"
          alt={formatMessage({
            id: 'CreatorProgram.sorareCreatorLogo',
            defaultMessage: 'Sorare Creators Program',
          })}
        />
      </picture>
      <Helmet
        script={[
          {
            type: 'application/ld+json',
            dangerouslySetInnerHTML: {
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                name: 'Sorare Creators Program',
                url: 'https://sorare.com/affiliate-program',
                image: {
                  '@type': 'ImageObject',
                  url: `${ORIGIN}${sorareCreatorsProgram}`,
                  name: 'Sorare Creators Program',
                  description,
                },
              }),
            },
          },
        ]}
      />
    </Vertical>
  );
};

const HowToToTile = () => {
  return (
    <TileWrapper gap={2}>
      {steps.map(step => (
        <StepContainer gap={2} key={step.name}>
          <TileName color="var(--c-white)">{step.name}</TileName>
          <TileText gap={2}>
            <TileTitle color="var(--c-white)">{step.title}</TileTitle>
            <LabelS color="var(--c-white)">{step.subtitle}</LabelS>
          </TileText>
        </StepContainer>
      ))}
    </TileWrapper>
  );
};

export const CreatorProgram = () => {
  return (
    <Vertical center gap={6}>
      <HeroContainer title="" gap={4}>
        <SorareCreatorLogo />
        <LabelL color="var(--c-white)">
          <FormattedMessage {...messages.heroSubtitle} />
        </LabelL>
        <Button size="large" color="secondary">
          <FormattedMessage {...messages.CTAButton} />
        </Button>
        <picture>
          <source srcSet={creatorsAvif} type="image/avif" />
          <CreatorsImg src={creators} fetchpriority="low" alt="" />
        </picture>
      </HeroContainer>
      <Section
        chipTitle={<FormattedMessage {...messages.howToChip} />}
        title={<FormattedMessage {...messages.howToTitle} />}
      >
        <HowToToTile />
      </Section>
    </Vertical>
  );
};
